"use client";

import {
  SESSION_CANDIDATE_COOKIE_NAME,
  SESSION_EXPIRED_COOKIE_NAME,
} from "@/lib/auth/session-expired-cookie";

const listeners = new Set<() => void>();

export type SessionRecoveryPhase = "active" | "checking" | "expired" | "restored";

export interface SessionRecoverySnapshot {
  phase: SessionRecoveryPhase;
  recoveryRequired: boolean;
  revision: number;
  expiredProfileReference?: string;
}

let snapshot: SessionRecoverySnapshot = {
  phase: "active",
  recoveryRequired: false,
  revision: 0,
};
const serverSnapshot: SessionRecoverySnapshot = {
  phase: "active",
  recoveryRequired: false,
  revision: 0,
};

function emitSnapshot(nextSnapshot: SessionRecoverySnapshot): void {
  snapshot = nextSnapshot;
  for (const listener of listeners) {
    listener();
  }
}

/** Marks the browser session expired while retaining the identity this tab must recover. */
export function notifySessionExpired(expiredProfileReference?: string): void {
  emitSnapshot({
    phase: "expired",
    recoveryRequired: true,
    revision: snapshot.revision + 1,
    expiredProfileReference: expiredProfileReference ?? snapshot.expiredProfileReference,
  });
}

/** Shows that this tab is actively verifying shared credentials. */
export function markSessionChecking(): void {
  emitSnapshot({ ...snapshot, phase: "checking", recoveryRequired: true });
}

/** Keeps recovery guidance visible after this tab installs verified credentials. */
export function markSessionRestored(): void {
  emitSnapshot({
    ...snapshot,
    phase: snapshot.recoveryRequired ? "restored" : "active",
  });
}

/** Returns a failed verification to the persistent expired state without retrying immediately. */
export function markSessionVerificationFailed(): void {
  emitSnapshot({ ...snapshot, phase: "expired", recoveryRequired: true });
}

/**
 * Un-latches the store when an authenticated call succeeds. Without this a session that
 * healed itself stays pinned behind a banner whose only button signs the user out.
 */
export function markSessionActive(): void {
  if (!snapshot.recoveryRequired && snapshot.phase === "active") {
    return;
  }

  emitSnapshot({
    phase: "active",
    recoveryRequired: false,
    revision: snapshot.revision,
  });
}

/** Subscribes React to the app-wide session-expiration state. */
export function subscribeToSessionExpiration(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Returns whether the current page has observed an expired session. */
export function getSessionExpirationSnapshot(): SessionRecoverySnapshot {
  return snapshot;
}

/** Keeps request rendering isolated from another browser tab's module state. */
export function getServerSessionExpirationSnapshot(): SessionRecoverySnapshot {
  return serverSnapshot;
}

/** Clears recovery guidance after this tab is verified and the user chooses to continue. */
export function resetSessionExpiration(): void {
  emitSnapshot({
    phase: "active",
    recoveryRequired: false,
    revision: snapshot.revision,
  });
}

export interface SessionCookieMarkers {
  expired?: string;
  candidate?: string;
}

/** Reads shared auth generations without consuming them before other tabs observe the change. */
export function readSessionCookieMarkers(
  browserDocument: Pick<Document, "cookie"> = document,
): SessionCookieMarkers {
  const markers: SessionCookieMarkers = {};
  for (const entry of browserDocument.cookie.split(";")) {
    const [name, value] = entry.trim().split("=", 2);
    if (name === SESSION_EXPIRED_COOKIE_NAME && value) {
      markers.expired = value;
    }
    if (name === SESSION_CANDIDATE_COOKIE_NAME && value) {
      markers.candidate = value;
    }
  }
  return markers;
}

interface CopyEditablePageTextInput {
  root?: ParentNode;
  writeText?: (text: string) => Promise<void>;
}

/**
 * Copies current form and editor text without persisting clinical content in browser storage.
 */
export async function copyEditablePageText({
  root = document,
  writeText = (text) => navigator.clipboard.writeText(text),
}: CopyEditablePageTextInput = {}): Promise<number> {
  const elements = root.querySelectorAll<HTMLElement>(
    'textarea, input:not([type="password"]):not([type="hidden"]), [contenteditable="true"]',
  );
  const values: string[] = [];

  for (const element of elements) {
    const value =
      element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement
        ? element.value.trim()
        : (element.innerText || element.textContent || "").trim();
    if (!!value.length && !values.includes(value)) {
      values.push(value);
    }
  }

  if (!values.length) {
    return 0;
  }

  await writeText(values.join("\n\n"));
  return values.length;
}
